// src/utils/mappers/workshopMappers.ts
/**
 * Мапперы для Workshop между слоями (Frontend ↔ Database)
 * Строго следует STYLEGUIDE.md для маппинга camelCase ↔ snake_case
 */

import { Workshop, DbWorkshop, WorkshopCreate, DbWorkshopInsert, WorkshopUpdate, DbWorkshopUpdate } from '@/types/entities/workshop';

/**
 * Преобразование DB Workshop в Frontend Workshop
 */
export const dbWorkshopToWorkshop = (dbWorkshop: DbWorkshop): Workshop => ({
  id: dbWorkshop.id,
  userId: dbWorkshop.user_id,
  createdAt: new Date(dbWorkshop.created_at),
  updatedAt: new Date(dbWorkshop.updated_at),
  
  rentalPointId: dbWorkshop.rental_point_id,
  orderId: dbWorkshop.order_id,
  clientId: dbWorkshop.client_id,
  clientName: dbWorkshop.client_name,
  
  // Основная информация
  title: dbWorkshop.title,
  description: dbWorkshop.description,
  type: dbWorkshop.type,
  
  // Для услуг
  serviceId: dbWorkshop.service_id,
  serviceName: dbWorkshop.service_name,
  servicePrice: dbWorkshop.service_price,
  quantity: dbWorkshop.quantity,
  
  // Для ремонтов
  itemName: dbWorkshop.item_name,
  itemBrand: dbWorkshop.item_brand,
  itemModel: dbWorkshop.item_model,
  serialNumber: dbWorkshop.serial_number,
  issueDescription: dbWorkshop.issue_description,
  repairType: dbWorkshop.repair_type,
  
  // Финансы
  estimatedCost: dbWorkshop.estimated_cost,
  actualCost: dbWorkshop.actual_cost,
  laborCost: dbWorkshop.labor_cost,
  partsCost: dbWorkshop.parts_cost,
  totalCost: dbWorkshop.total_cost,
  
  // Статус и временные рамки
  status: dbWorkshop.status,
  priority: dbWorkshop.priority,
  startDate: dbWorkshop.start_date ? new Date(dbWorkshop.start_date) : undefined,
  estimatedCompletionDate: dbWorkshop.estimated_completion_date ? new Date(dbWorkshop.estimated_completion_date) : undefined,
  completedAt: dbWorkshop.completed_at ? new Date(dbWorkshop.completed_at) : undefined,
  
  // Дополнительная информация
  technicianId: dbWorkshop.technician_id,
  technicianName: dbWorkshop.technician_name,
  technicianNotes: dbWorkshop.technician_notes,
  customerNotes: dbWorkshop.customer_notes,
  internalNotes: dbWorkshop.internal_notes,
  
  // Флаги
  urgentWork: dbWorkshop.urgent_work,
  warrantyWork: dbWorkshop.warranty_work,
  requiresApproval: dbWorkshop.requires_approval,
  customerApproved: dbWorkshop.customer_approved,
});

/**
 * Преобразование Frontend Workshop в DB Workshop
 */
export const workshopToDbWorkshop = (workshop: Workshop): DbWorkshop => ({
  id: workshop.id,
  user_id: workshop.userId,
  created_at: workshop.createdAt.toISOString(),
  updated_at: workshop.updatedAt.toISOString(),
  
  rental_point_id: workshop.rentalPointId,
  order_id: workshop.orderId,
  client_id: workshop.clientId,
  client_name: workshop.clientName,
  
  // Основная информация
  title: workshop.title,
  description: workshop.description,
  type: workshop.type,
  
  // Для услуг
  service_id: workshop.serviceId,
  service_name: workshop.serviceName,
  service_price: workshop.servicePrice,
  quantity: workshop.quantity,
  
  // Для ремонтов
  item_name: workshop.itemName,
  item_brand: workshop.itemBrand,
  item_model: workshop.itemModel,
  serial_number: workshop.serialNumber,
  issue_description: workshop.issueDescription,
  repair_type: workshop.repairType,
  
  // Финансы
  estimated_cost: workshop.estimatedCost,
  actual_cost: workshop.actualCost,
  labor_cost: workshop.laborCost,
  parts_cost: workshop.partsCost,
  total_cost: workshop.totalCost,
  
  // Статус и временные рамки
  status: workshop.status,
  priority: workshop.priority,
  start_date: workshop.startDate?.toISOString(),
  estimated_completion_date: workshop.estimatedCompletionDate?.toISOString(),
  completed_at: workshop.completedAt?.toISOString(),
  
  // Дополнительная информация
  technician_id: workshop.technicianId,
  technician_name: workshop.technicianName,
  technician_notes: workshop.technicianNotes,
  customer_notes: workshop.customerNotes,
  internal_notes: workshop.internalNotes,
  
  // Флаги
  urgent_work: workshop.urgentWork,
  warranty_work: workshop.warrantyWork,
  requires_approval: workshop.requiresApproval,
  customer_approved: workshop.customerApproved,
});

/**
 * Преобразование WorkshopCreate в DbWorkshopInsert
 */
export const workshopCreateToDbWorkshopInsert = (
  workshopCreate: WorkshopCreate,
  userId: string
): DbWorkshopInsert => ({
  user_id: userId,
  rental_point_id: workshopCreate.rentalPointId,
  order_id: workshopCreate.orderId,
  client_id: workshopCreate.clientId,
  client_name: workshopCreate.clientName,
  title: workshopCreate.title,
  description: workshopCreate.description,
  type: workshopCreate.type,
  service_id: workshopCreate.serviceId,
  item_name: workshopCreate.itemName,
  issue_description: workshopCreate.issueDescription,
  estimated_cost: workshopCreate.estimatedCost,
  status: 'pending', // Статус по умолчанию
  priority: workshopCreate.priority || 'medium', // Приоритет по умолчанию
  estimated_completion_date: workshopCreate.estimatedCompletionDate?.toISOString(),
  customer_notes: workshopCreate.customerNotes,
  urgent_work: workshopCreate.urgentWork || false,
});

/**
 * Преобразование WorkshopUpdate в DbWorkshopUpdate
 */
export const workshopUpdateToDbWorkshopUpdate = (workshopUpdate: WorkshopUpdate): DbWorkshopUpdate => ({
  title: workshopUpdate.title,
  description: workshopUpdate.description,
  status: workshopUpdate.status,
  priority: workshopUpdate.priority,
  actual_cost: workshopUpdate.actualCost,
  labor_cost: workshopUpdate.laborCost,
  parts_cost: workshopUpdate.partsCost,
  total_cost: workshopUpdate.totalCost,
  start_date: workshopUpdate.startDate?.toISOString(),
  estimated_completion_date: workshopUpdate.estimatedCompletionDate?.toISOString(),
  completed_at: workshopUpdate.completedAt?.toISOString(),
  technician_id: workshopUpdate.technicianId,
  technician_name: workshopUpdate.technicianName,
  technician_notes: workshopUpdate.technicianNotes,
  customer_notes: workshopUpdate.customerNotes,
  internal_notes: workshopUpdate.internalNotes,
  customer_approved: workshopUpdate.customerApproved,
});

/**
 * Массовое преобразование DB Workshop в Frontend Workshop
 */
export const dbWorkshopsToWorkshops = (dbWorkshops: DbWorkshop[]): Workshop[] => {
  return dbWorkshops.map(dbWorkshopToWorkshop);
};

/**
 * Массовое преобразование Frontend Workshop в DB Workshop
 */
export const workshopsToDbWorkshops = (workshops: Workshop[]): DbWorkshop[] => {
  return workshops.map(workshopToDbWorkshop);
};
